import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import pushRoute from '@utils/pushRoute';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Button from '@base/Button/Button';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import H6 from '@base/H6/H6';
import H3 from '@base/H3/H3';
import Tags from '@base/Tags/Tags';
import Course from '@models/Course';
import { Spacings, breakpoints } from '@styles/index';

const status = {
  isOngoing: { title: 'ONGOING COURSE', color: 'var(--color-i-red)' },
  isCompleted: { title: 'COMPLETED COURSE', color: 'var(--color-i-green)' },
  isUpcoming: { title: 'UPCOMING COURSE', color: 'var(--color-i-green)' }
};

const Wrapper = styled.div`
  grid-column: span 24;
  display: flex;
  padding: ${Spacings.SPACING_12B};
  border-radius: ${Spacings.SPACING_2B};
  background: var(--color-base-1);
  box-shadow: ${boxShadowGenerator({
    blur: Spacings.SPACING_5B
  })};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    padding: 0;
    flex-direction: column;
    box-shadow: none;
    background-color: var(--color-base-1);
    margin-bottom: 24px;
  }
`;

const CourseThumbnail = styled(Thumbnail)`
  height: 171px;
  width: 304px;
  border-radius: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    height: 180px;
  }
`;

const CourseTitle = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
  width: 448px;
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    margin: 8px 0 12px;
    font-size: 22px;
    line-height: 150%;
  }
`;

const CourseInfo = styled.div`
  margin-left: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 24px 0 0;
    & > h6 {
      font-size: 14px;
    }
  }
`;

const LanguageTag = styled(Tags)`
  width: fit-content;
`;

const StatusTag = styled(Tags)`
  width: fit-content;
  display: flex;
  margin-right: ${Spacings.SPACING_2B};
  align-items: center;
`;

const TagWrapper = styled.div`
  display: flex;
`;

const Status = styled.div`
  width: ${Spacings.SPACING_2B};
  height: ${Spacings.SPACING_2B};
  background-color: ${({ color }) => color};
  border-radius: 50%;
  margin-right: ${Spacings.SPACING_1B};
`;

const CourseButton = styled(Button)`
  margin-left: 80px;
  margin-top: 38px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 24px 0 0;
    width: 100%;
    font-size: 14px;
  }
`;

const BatchCourseHeader = ({ course }) => {
  const {
    coverPhoto,
    name,
    authors,
    language,
    isOngoing,
    isUpcoming,
    isCompleted,
    slug,
    uid
  } = course;

  const handleClick = () => {
    pushRoute('/course/[slug]/[courseUID]', `/course/${slug}/${uid}`);
  };

  const checkStatus = () => {
    if (isCompleted) return 'isCompleted';
    if (isUpcoming) return 'isUpcoming';
    if (isOngoing) return 'isOngoing';
  };
  const courseStatus = checkStatus();
  return (
    <Wrapper>
      <CourseThumbnail imgUrl={coverPhoto} />
      <CourseInfo>
        <TagWrapper>
          <StatusTag type="hollow">
            <Status color={status[courseStatus]?.color} />
            {status[courseStatus]?.title}
          </StatusTag>
          <LanguageTag type="hollow">{language}</LanguageTag>
        </TagWrapper>
        <CourseTitle>{name}</CourseTitle>
        <H6 color="var(--color-text-primary)">
          {Course.getAuthorsName(authors)}
        </H6>
      </CourseInfo>
      <CourseButton
        label="Go to course page"
        variant="hollow"
        color="black"
        onClick={handleClick}
      />
    </Wrapper>
  );
};

export default BatchCourseHeader;

BatchCourseHeader.propTypes = {
  course: PropTypes.objectOf(PropTypes.any)
};

BatchCourseHeader.defaultProps = {
  course: {}
};
